import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react"

const responsive = (breakPoints) => {
  if (!breakPoints) return

  const sortedBreakPoints =
    breakPoints.sort((a, b) => {
      if (a.point >= b.point) return -1

      return 1
    })

  for (let breakPoint of sortedBreakPoints) {
    if (window.innerWidth >= breakPoint.point)
      return breakPoint
  }

  return breakPoints[breakPoints.length - 1]
}

const VirtualGrid = (props) => {
  const {
    data,
    sectionId,
    containerRef,
    offset = 1,
    renderItem,
    breakPoints,
    sameHeightAndWidth
  } = props

  const breakPoint = responsive(breakPoints)

  const initialColumnsState =
    breakPoint.numberOfColumns

  const initialHeightState =
    breakPoint.rowHeight

  const [columns, setColumns] = useState(initialColumnsState)
  const [itemHeight, setItemHeight] = useState(initialHeightState)
  const [rowPositions, setRowPosition] = useState({ startRow: 0, endRow: 0 })

  const wrapperRef = useRef(null)
  const previousStartRow = useRef()

  const handleResize =
    useCallback(() => {
      if (!containerRef.current) return

      previousStartRow.current = undefined

      showViewableItems()

      const breakPoint = responsive(breakPoints)

      const columns =
        breakPoint.numberOfColumns

      const height =
        sameHeightAndWidth
          ? wrapperRef.current.clientWidth / columns
          : breakPoint.rowHeight

      setColumns(columns)

      setItemHeight(height)
    }, [data, columns, itemHeight])

  const showViewableItems =
    useCallback(() => {
      if (data.length === 0) return

      const top =
        containerRef.current.scrollTop -
        (wrapperRef.current.offsetTop - containerRef.current.offsetTop)

      let startRow =
        Math.max(((top / itemHeight) | 0), 0)

      const viewPortHeight =
        containerRef.current.clientHeight

      const totalRows = Math.ceil(data.length / columns)

      if (previousStartRow.current === startRow) return
      else previousStartRow.current = startRow

      const numberOfRows = Math.ceil(viewPortHeight / itemHeight)

      const endRow =
        Math.min(startRow + numberOfRows + offset, totalRows)

      startRow =
        Math.max(startRow - offset, 0)

      setRowPosition({
        startRow,
        endRow
      })
    }, [data, columns, itemHeight])

  const handleScroll =
    useCallback(() => {
      showViewableItems()

      const breakPoint = responsive(breakPoints)

      const columns =
        breakPoint.numberOfColumns

      const height =
        sameHeightAndWidth
          ? wrapperRef.current.clientWidth / columns
          : breakPoint.rowHeight

      setColumns(columns)

      setItemHeight(height)
    }, [data, columns, itemHeight])

  const initialize = () => {
    previousStartRow.current = undefined

    showViewableItems()
  }

  useLayoutEffect(() => {
    initialize()
  }, [data, columns, itemHeight])

  useLayoutEffect(() => {
    if ("ResizeObserver" in window) {
      const observer = new ResizeObserver(handleResize)

      containerRef.current &&
        observer.observe(containerRef.current)

      containerRef.current.addEventListener("scroll", handleScroll)

      return () => {
        if (containerRef.current) {
          observer.unobserve(containerRef.current)

          containerRef.current.removeEventListener("scroll", handleScroll)
        }
      }
    } else {
      handleResize()

      window.addEventListener("resize", handleResize)

      containerRef.current.addEventListener("scroll", handleScroll)

      return () => {
        window.removeEventListener("resize", handleResize)

        containerRef.current &&
          containerRef.current.removeEventListener("scroll", handleScroll)
      }
    }
  }, [data, columns, itemHeight])

  const totalRows =
    Math.ceil(data.length / columns)

  const paddingTop =
    `${rowPositions.startRow * itemHeight}px`

  const paddingBottom =
    `${(totalRows - rowPositions.endRow) * itemHeight}px`

  const items =
    useMemo(() => (
      data.slice(
        rowPositions.startRow * columns,
        rowPositions.endRow * columns
      )
    ), [
      columns,
      rowPositions.startRow,
      rowPositions.endRow
    ])

  return (
    <div
      ref={wrapperRef}
      style={{
        display: "flex",
        flexWrap: "wrap",
        paddingTop,
        paddingBottom
      }}
    >
      {items.map((item, index) =>
        <div
          key={`${sectionId}${item}`}
          style={{
            width: `calc(100% / ${columns})`,
            height: `${itemHeight}px`
          }}
        >
          {renderItem(item, index)}
        </div>
      )}
    </div>
  )
}

export default VirtualGrid